import type { BoardContent, Cell, CellSpec, Difficulty } from './types';
import type { DifficultyParams } from './config';
import { Rng } from './rng';
import { idOf, rowOf, colOf, neighborId, DIRS } from './grid';

export interface BoardState {
  topic: string;
  rule: string;
  rows: number;
  cols: number;
  cells: Cell[];
  quota: number;
  correctCount: number;
  params: DifficultyParams;
  humanStart: number;
  aiStart: number;
}

function makeCell(id: number, cols: number, kind: Cell['kind'], spec?: CellSpec): Cell {
  return {
    id,
    row: rowOf(id, cols),
    col: colOf(id, cols),
    kind,
    text: spec?.text ?? '',
    displayText: spec?.text ?? '',
    difficulty: (spec?.difficulty ?? 1) as Difficulty,
    explanation: spec?.explanation ?? '',
    eaten: false,
    eatenBy: null,
    armed: false,
    armedUntilMs: 0,
    revealedUntilMs: 0,
    lying: false,
    lyingUntilMs: 0,
  };
}

/** True when every non-wall cell is one connected component (BFS from `start`, a reserved non-wall
 *  spawn). Full connectivity is the strongest cheap invariant: it guarantees BOTH spawns can reach
 *  EVERY correct cell no matter where correct placement lands afterwards. */
function allPassableConnected(rows: number, cols: number, total: number, walls: Set<number>, start: number): boolean {
  const target = total - walls.size;
  const seen = new Set<number>([start]);
  const queue = [start];
  while (queue.length > 0) {
    const id = queue.pop() as number;
    for (const d of DIRS) {
      const nb = neighborId(id, d, rows, cols);
      if (nb === null || walls.has(nb) || seen.has(nb)) continue;
      seen.add(nb);
      queue.push(nb);
    }
  }
  return seen.size === target;
}

/** Is this interior cell (4 orthogonal neighbors)? Used to bias fenced-correct placement. */
function isInterior(id: number, rows: number, cols: number): boolean {
  const r = rowOf(id, cols);
  const c = colOf(id, cols);
  return r > 0 && c > 0 && r < rows - 1 && c < cols - 1;
}

/**
 * Lay authored content onto the grid. Correct cells are interspersed with decoys; a
 * `fenceRatio` share of correct answers is biased to interior cells so they sit behind
 * decoys (The Gauntlet). Player and AI start on opposite-corner decoy cells.
 */
export function buildBoard(content: BoardContent, params: DifficultyParams, rng: Rng): BoardState {
  const { rows, cols } = params;
  const total = rows * cols;
  const humanStart = idOf(rows - 1, 0, cols); // bottom-left
  const aiStart = idOf(0, cols - 1, cols); // top-right
  const reserved = new Set<number>([humanStart, aiStart]);

  const correctPool = content.cells.filter((c) => c.kind === 'correct');
  const decoyPool = content.cells.filter((c) => c.kind === 'decoy');
  // Defensive: a board with no decoys would be unplayable — synthesize a generic one.
  if (decoyPool.length === 0) decoyPool.push({ text: '???', kind: 'decoy' });

  const free = rng.shuffle(Array.from({ length: total }, (_, i) => i).filter((i) => !reserved.has(i)));

  // Walls — connectivity-guarded reject-resample (2026-07-02 reach-probe: naive 3-wall placement
  // sealed a spawn or pocketed cells on ~1.5-2.5% of seeds — every rival match's board 5, plus a
  // shared-seed daily-MAZE global brick every few months). Requirement: the NON-wall graph stays one
  // connected component, which implies both spawns reach every future correct regardless of where
  // corrects land. Deterministic + rng-stream-pure: candidates come off the top of the already-
  // shuffled deck; a rejected draw rotates to the BOTTOM (cells stay placeable as content) and the
  // next draw is tried. If a wall count can't satisfy connectivity in bounded draws, drop one wall
  // (walls=0 is trivially connected), so buildBoard always terminates with a fully-reachable board.
  const wallSet = new Set<number>();
  let want = Math.min(params.walls, free.length);
  const MAX_DRAWS = 8;
  while (want > 0) {
    let placed = false;
    for (let attempt = 0; attempt < MAX_DRAWS && free.length >= want; attempt++) {
      const candidate = free.slice(free.length - want);
      if (allPassableConnected(rows, cols, total, new Set(candidate), humanStart)) {
        for (let i = 0; i < want; i++) wallSet.add(free.pop() as number);
        placed = true;
        break;
      }
      const rejected = free.splice(free.length - want, want);
      free.unshift(...rejected);
    }
    if (placed) break;
    want--;
  }

  // Correct placements — fence a share by preferring interior cells.
  const nCorrect = Math.min(params.targetCorrect, correctPool.length);
  const nFenced = Math.floor(params.fenceRatio * nCorrect);
  const interiorFree = free.filter((id) => isInterior(id, rows, cols));
  const correctSet = new Set<number>();
  for (let i = 0; i < nFenced && interiorFree.length > 0; i++) {
    const id = interiorFree.pop() as number;
    correctSet.add(id);
  }
  for (const id of free) {
    if (correctSet.size >= nCorrect) break;
    if (!correctSet.has(id)) correctSet.add(id);
  }

  // Assemble cells.
  const cells: Cell[] = new Array(total);
  let ci = 0;
  let di = 0;
  for (let id = 0; id < total; id++) {
    if (wallSet.has(id)) {
      cells[id] = makeCell(id, cols, 'wall');
    } else if (correctSet.has(id)) {
      cells[id] = makeCell(id, cols, 'correct', correctPool[ci % correctPool.length]);
      ci++;
    } else {
      // reserved start cells + everything else become decoys
      cells[id] = makeCell(id, cols, 'decoy', decoyPool[di % decoyPool.length]);
      di++;
    }
  }

  const correctCount = correctSet.size;
  const quota = Math.max(1, Math.min(params.quota, correctCount));

  return {
    topic: content.topicRaw,
    rule: content.ruleStatement,
    rows,
    cols,
    cells,
    quota,
    correctCount,
    params,
    humanStart,
    aiStart,
  };
}
